import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import type { GetFilter, GetPagination } from '@shared/interfacres/request-related';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DEFAULT_PAGE_SIZE } from '@shared/constants/basic';
import { CreateUserPayload, User } from '@shared/interfacres/user';
import { UsersManagerService } from '@data-access/manager/services/users-manager.service';


interface UsersState {
  items: User[];
  total: number;
  name: string;
  isLoading: boolean;
  filter: GetFilter;
  pagination: GetPagination;
}

const initialState: UsersState = {
  items: [],
  total: 0,
  name: '',
  isLoading: false,
  filter: { query: '', order: 'asc' },
  pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE }
};

export const UsersManagerStore = signalStore(
  withState(initialState),

  withMethods((store, service = inject(UsersManagerService), snackBar = inject(MatSnackBar)) => ({

    getPerformanceTypes() {
      return service.getPerformanceTypes();
    },
    getByName(name: string) {
      patchState(store, () => ({
        name
      }));
      return this.getFiltered();
    },

    setPagination(page: number, pageSize: number) {
      patchState(store, (state) => ({
        pagination: {
          ...state.pagination,
          page,
          pageSize,
        }
      }));
      return this.getFiltered();
    },

    setFilter(query: string, order: 'asc' | 'desc') {
      patchState(store, (state) => ({
        filter: {
          ...state.filter,
          query,
          order,
        }
      }));
      return this.getFiltered();
    },

    async getFiltered() {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        const name = store.name() || '';

        const dataWithPagination = await firstValueFrom(service.getFilteredList(name, store.filter(), store.pagination()));
        patchState(store, () => ({
          isLoading: false,
          items: dataWithPagination.data.map((item: User) => {
            return {
              ...item
            }
          }),
          total: dataWithPagination.pagination.total
        }));

      } catch (e) {
        snackBar.open('Error ', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },

    clearCriteria() {
      patchState(store, () => ({
        name: '',
        filter: { query: '', order: 'asc' } as GetFilter,
        pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE }
      }));
    },
    async update(id: string, payload: Partial<CreateUserPayload>) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        await firstValueFrom(service.update(id, payload));

        return this.getFiltered();

      } catch (e) {
        snackBar.open('Error during update', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },
    async approval(id: string, isApproved: boolean) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        const res = await firstValueFrom(service.approval(id, isApproved));
        snackBar.open('Success', res.message, { duration: 5000, horizontalPosition: 'end', panelClass: 'success-toast' });
        return this.getFiltered();

      } catch (e) {
        snackBar.open('Error during update', (e as { error : {message: string} }).error?.message, { duration: 10000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },
    async reject(id: string, isRejected: boolean) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        await firstValueFrom(service.reject(id, isRejected));

        return this.getFiltered();

      } catch (e) {
        snackBar.open('Error during update', (e as { error : {message: string} }).error?.message, { duration: 10000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },
    async delete(id: string) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        await firstValueFrom(service.delete(id));

        return this.getFiltered();

      } catch (e) {
        snackBar.open('Error during delete', (e as { error : {message: string} }).error?.message, { duration: 10000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },
  }))
);
